/**
 * UserRole - Extended role hierarchy for the platform (8 levels)
 * Coexists with existing AppRole system (client, agent, manager, admin)
 */

export enum UserRole {
  CANDIDATE = 'candidate',
  FREELANCER = 'freelancer',
  EMPLOYER = 'employer',
  MANAGER = 'manager',
  HR_MANAGER = 'hr_manager',
  DEPARTMENT_HEAD = 'department_head',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
}

// Role hierarchy levels (higher = more access)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.CANDIDATE]: 1,
  [UserRole.FREELANCER]: 2,
  [UserRole.EMPLOYER]: 3,
  [UserRole.MANAGER]: 4,
  [UserRole.HR_MANAGER]: 5,
  [UserRole.DEPARTMENT_HEAD]: 6,
  [UserRole.ADMIN]: 7,
  [UserRole.SUPER_ADMIN]: 8,
};

// Check if a role meets the minimum required level
export function hasMinimumRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

// Map existing AppRole to extended UserRole
export function mapAppRoleToUserRole(appRole: string, internalRole?: string | null): UserRole {
  if (internalRole === 'super_admin') return UserRole.SUPER_ADMIN;
  if (internalRole && ['operations_manager', 'account_manager', 'team_leader', 'qa_specialist', 'wfm_planner', 'trainer_kb_manager', 'it_delivery_manager', 'incident_manager', 'finance_admin'].includes(internalRole)) {
    return UserRole.ADMIN;
  }
  // Agents stay as agents — they should NOT access the freelancer portal
  // They use the AgentLayout/AgentPortal instead
  if (internalRole === 'agent') return UserRole.CANDIDATE;

  switch (appRole) {
    case 'admin': return UserRole.ADMIN;
    case 'manager': return UserRole.MANAGER;
    case 'agent': return UserRole.CANDIDATE; // Agents use AgentLayout, not FreelancerLayout
    case 'client': return UserRole.EMPLOYER;
    default: return UserRole.CANDIDATE;
  }
}

// Role display info
export const ROLE_LABELS: Record<UserRole, { en: string; fr: string }> = {
  [UserRole.CANDIDATE]: { en: 'Candidate', fr: 'Candidat' },
  [UserRole.FREELANCER]: { en: 'Freelancer', fr: 'Freelancer' },
  [UserRole.EMPLOYER]: { en: 'Employer', fr: 'Entreprise' },
  [UserRole.MANAGER]: { en: 'Manager', fr: 'Manager' },
  [UserRole.HR_MANAGER]: { en: 'HR Manager', fr: 'Responsable RH' },
  [UserRole.DEPARTMENT_HEAD]: { en: 'Department Head', fr: 'Directeur de Service' },
  [UserRole.ADMIN]: { en: 'Administrator', fr: 'Administrateur' },
  [UserRole.SUPER_ADMIN]: { en: 'Super Administrator', fr: 'Super Administrateur' },
};

// Dashboard paths per role
export const ROLE_DASHBOARD_PATHS: Record<UserRole, string> = {
  [UserRole.CANDIDATE]: '/candidate/dashboard',
  [UserRole.FREELANCER]: '/freelancer/dashboard',
  [UserRole.EMPLOYER]: '/employer/dashboard',
  [UserRole.MANAGER]: '/manager/dashboard',
  [UserRole.HR_MANAGER]: '/hr/dashboard',
  [UserRole.DEPARTMENT_HEAD]: '/director/dashboard',
  [UserRole.ADMIN]: '/admin/dashboard',
  [UserRole.SUPER_ADMIN]: '/admin/dashboard',
};
